/**
 * Self Report Analytics Logger
 * Logs self-report submission events for analytics and auditing
 * Tracks user-reported results vs model results for analysis
 */

import { sentryLogMessage } from "services/sentryLogger";
import type { SelfReportedResult } from "types/models";
import type { ResultStatus } from "types/imageRecognition";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";
import { formatImageUrlsForSentry } from "utils/sentryHelpers";
import { getModelMetaDataTags } from "./models";
// --- TinyURL shortening (commented out — download URLs now go into extra data, no 200-char limit) ---
// import { shortenUrls } from "utils/shortenUrl";

interface SelfReportAnalyticsData {
  timestamp: number;
  selfReportedResult: SelfReportedResult;
  modelResult: ResultStatus;
  imageDownloadUrls?: Array<string>;
}

/**
 * Logs self-report analytics data to Sentry
 * Used for tracking user-reported results vs model results for analysis
 * 
 * @param data - Self-report analytics data
 */
export const logSelfReportAnalytics = async (data: SelfReportAnalyticsData): Promise<void> => {
  console.log('logSelfReportAnalytics: Logging self-report analytics', data);
  try {
    const { timestamp, selfReportedResult, modelResult, imageDownloadUrls } = data;
    
    // Format timestamp as readable date string
    const formattedTimestamp = new Date(timestamp).toISOString();
    
    // Determine if there's a mismatch between self-reported and model result
    const resultMatch = 
      (selfReportedResult === 'positive' && modelResult === 'POSITIVE') ||
      (selfReportedResult === 'negative' && modelResult === 'NEGATIVE')
        ? 'match'
        : 'mismatch';

    // --- TinyURL shortening (commented out — download URLs now go into extra data) ---
    // const shortenedLinks = await shortenUrls({
    //   imagesZip: imagesZip,
    //   maxQualityImageUrl: maxQualityImageUrl,
    // });

    sentryLogMessage({
      message: `Self-report ${resultMatch}`,
      level: resultMatch === 'match' ? 'info' : 'warning',
      tags: {
        event_type: SENTRY_EVENT_TYPES.USER_SELF_REPORTED_RESULT,
        component: 'SelfReportScreen',
        category: 'self_report_analytics',
        self_reported_result: selfReportedResult,
        model_result: modelResult,
        result_match: resultMatch,
        ...getModelMetaDataTags(),
        // --- Commented out — replaced by per-image download URLs in extra ---
        // images_zip_url: shortenedLinks.imagesZip,
        // max_quality_image_url: shortenedLinks.maxQualityImageUrl,
      },
      extra: {
        timestamp: formattedTimestamp,
        selfReportedResult,
        modelResult,
        resultMatch,
        ...formatImageUrlsForSentry(imageDownloadUrls),
      },
    });
    
  } catch (error) {
    // Silently fail - don't break the app if analytics logging fails
    console.error('Failed to log self-report analytics:', error);
  }
};
